import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Pane, Heading, Text, Table, Spinner, Button, Badge, Code, IconButton } from 'evergreen-ui';
import { ChevronDownIcon, ChevronUpIcon } from 'evergreen-ui';
import { GET_RECENT_VISITORS, GET_PUBLISHER_ACTIVITIES } from '../../../graphql/queries/Auth/QueryAuth';

/**
 * VisitorsBox component displays unique publishers with their most recent activity
 * Each publisher row can be expanded to show detailed activity logs
 */
const VisitorsBox = () => {
  const [expandedPublishers, setExpandedPublishers] = useState({});
  const [showDebug, setShowDebug] = useState(false);

  const { data, loading, error } = useQuery(GET_RECENT_VISITORS, {
    variables: { limit: 50 }, // Fetch more to ensure we have enough unique publishers
    fetchPolicy: 'network-only',
    pollInterval: 60000 // Refresh every minute
  });

  // Helper function to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Process the data to get unique publishers with their most recent activity
  const publisherData = useMemo(() => {
    try {
      if (!data?.recentVisitors) return [];

      const allActivities = data.recentVisitors;
      const publisherMap = new Map();

      // Group activities by publisher
      allActivities.forEach(activity => {
        try {
          // Skip activities without publisher or missing required data
          if (!activity?.publisher?.id || !activity?.user || !activity?.created) return;

          const publisherId = activity.publisher.id;
          if (!publisherMap.has(publisherId)) {
            // Initialize with first activity for this publisher
            publisherMap.set(publisherId, {
              publisher: activity.publisher,
              latestActivity: activity,
              activities: [activity]
            });
          } else {
            const current = publisherMap.get(publisherId);

            // Add this activity to the list
            current.activities.push(activity);

            // Update latest activity if this one is newer
            // Use try/catch to handle invalid date formats
            try {
              if (new Date(activity.created) > new Date(current.latestActivity.created)) {
                current.latestActivity = activity;
              }
            } catch (dateError) {
              console.error('Error comparing dates:', dateError);
              // Keep the existing latest activity if date comparison fails
            }
          }
        } catch (activityError) {
          console.error('Error processing activity:', activityError, activity);
          // Continue processing other activities even if one fails
        }
      });

      // Convert map to array and sort by latest activity date (newest first)
      return Array.from(publisherMap.values())
        .sort((a, b) => {
          try {
            return new Date(b.latestActivity.created) - new Date(a.latestActivity.created);
          } catch (sortError) {
            console.error('Error sorting activities:', sortError);
            return 0; // Return 0 to maintain original order if comparison fails
          }
        });
    } catch (error) {
      console.error('Error processing publisher data:', error);
      return []; // Return empty array on any error to ensure component doesn't crash
    }
  }, [data?.recentVisitors]);

  // Helper function to safely get user display name
  const getUserDisplayName = (user) => {
    try {
      if (!user) return 'Unknown User';

      if (user.firstName || user.lastName) {
        return `${user.firstName || ''} ${user.lastName || ''}`.trim();
      }
      return user.username || user.email || 'Unknown User';
    } catch (error) {
      console.error('Error getting user display name:', error);
      return 'Unknown User';
    }
  };

  // Function to get action type badge
  const getActionTypeBadge = (actionType) => {
    switch(actionType) {
      case 'login':
        return <Badge color="green">Login</Badge>;
      case 'expense_created':
        return <Badge color="blue">Expense</Badge>;
      case 'product_created':
        return <Badge color="purple">Product</Badge>;
      case 'creator_created':
        return <Badge color="orange">Creator</Badge>;
      case 'customer_created':
        return <Badge color="teal">Customer</Badge>;
      case 'order_created':
        return <Badge color="red">Order</Badge>;
      case 'royalty_created':
        return <Badge color="yellow">Royalty</Badge>;
      case 'royalty_payment_created':
        return <Badge color="yellow">Royalty Payment</Badge>;
      default:
        return <Badge color="neutral">{actionType}</Badge>;
    }
  };

  // Toggle the expanded state of a publisher
  const toggleExpanded = (publisherId) => {
    setExpandedPublishers(prev => ({
      ...prev,
      [publisherId]: !prev[publisherId]
    }));
  };

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
        <Spinner />
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane background="redTint" padding={16} borderRadius={4}>
        <Text color="red500">Error loading recent visitors: {error.message}</Text>
      </Pane>
    );
  }

  return (
    <Pane background="tint1" padding={16} borderRadius={4} marginBottom={24}>
      <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
        <Heading size={500}>Recent Publisher Activity</Heading>
        <Button onClick={() => setShowDebug(!showDebug)}>
          {showDebug ? 'Hide Debug' : 'Show Debug'}
        </Button>
      </Pane>

      {showDebug && (
        <Pane background="yellowTint" padding={8} marginBottom={16} borderRadius={4}>
          <Heading size={300} marginBottom={8}>Debug Information:</Heading>
          <Text>Total activities received: {data?.recentVisitors?.length || 0}</Text>
          <Text>Unique publishers: {publisherData.length}</Text>
          <Text>Activity types: {[...new Set((data?.recentVisitors || []).map(v => v.actionType))].join(', ')}</Text>
        </Pane>
      )}

      {publisherData.length === 0 ? (
        <Text>No recent activity found.</Text>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Publisher</Table.TextHeaderCell>
            <Table.TextHeaderCell>Last Active User</Table.TextHeaderCell>
            <Table.TextHeaderCell>Last Activity Time</Table.TextHeaderCell>
            <Table.TextHeaderCell>Last Action</Table.TextHeaderCell>
            <Table.TextHeaderCell width={50}>Details</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {publisherData.map(({publisher, latestActivity, activities}) => (
              <React.Fragment key={publisher.id}>
                <Table.Row>
                  <Table.TextCell>{publisher.name}</Table.TextCell>
                  <Table.TextCell>{getUserDisplayName(latestActivity.user)}</Table.TextCell>
                  <Table.TextCell>{formatDate(latestActivity.created)}</Table.TextCell>
                  <Table.TextCell>{getActionTypeBadge(latestActivity.actionType)}</Table.TextCell>
                  <Table.Cell>
                    <IconButton
                      icon={expandedPublishers[publisher.id] ? ChevronUpIcon : ChevronDownIcon}
                      onClick={() => toggleExpanded(publisher.id)}
                      appearance="minimal"
                    />
                  </Table.Cell>
                </Table.Row>

                {expandedPublishers[publisher.id] && (
                  <Table.Row height="auto">
                    <Table.Cell colSpan={5} paddingY={12} background="tint2">
                      <Pane
                        padding={16}
                        maxWidth="100%"
                        overflowX="auto"
                        marginX="-16px" // Negative margin to extend beyond parent padding
                        width="calc(100% + 32px)" // Compensate for the negative margins
                      >
                        <Heading size={300} marginBottom={16}>
                          Activity for {publisher.name}
                        </Heading>
                        <Table width="100%">
                          <Table.Head height={32}>
                            <Table.TextHeaderCell width="18%">User</Table.TextHeaderCell>
                            <Table.TextHeaderCell width="18%">Date/Time</Table.TextHeaderCell>
                            <Table.TextHeaderCell width="14%">Action</Table.TextHeaderCell>
                            <Table.TextHeaderCell>Details</Table.TextHeaderCell>
                          </Table.Head>
                          <Table.Body>
                            {activities
                              .sort((a, b) => new Date(b.created) - new Date(a.created))
                              .map(activity => (
                                <Table.Row key={activity.id} height={36}>
                                  <Table.TextCell>{getUserDisplayName(activity.user)}</Table.TextCell>
                                  <Table.TextCell>{formatDate(activity.created)}</Table.TextCell>
                                  <Table.TextCell>{getActionTypeBadge(activity.actionType)}</Table.TextCell>
                                  <Table.Cell>
                                    <Text fontSize="small" wordBreak="break-word">{activity.actionDetails || 'No details'}</Text>
                                  </Table.Cell>
                                </Table.Row>
                              ))
                            }
                          </Table.Body>
                        </Table>
                      </Pane>
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table>
      )}
    </Pane>
  );
};

export default VisitorsBox;
